import { JSX, Show, onCleanup, onMount, createEffect, createSignal } from 'solid-js';
import { Portal } from 'solid-js/web';
import { animate } from 'motion';

interface ModalBaseProps {
  id: string;
  title: string;
  open: boolean;
  onClose: () => void;
  size?: 'sm' | 'md' | 'lg';
  children: JSX.Element;
}

export default function ModalBase(props: ModalBaseProps) {
  let overlayRef: HTMLDivElement | undefined;
  let panelRef: HTMLDivElement | undefined;
  const [visible, setVisible] = createSignal(false);

  const focusableSelector = 'button:not([disabled]),a[href],input:not([disabled]):not([type="hidden"]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  const setInitialFocus = () => {
    if (!panelRef) return;
    const auto = panelRef.querySelector<HTMLElement>('[data-autofocus]');
    if (auto) { try { auto.focus(); return; } catch {}
    }
    const items = Array.from(panelRef.querySelectorAll<HTMLElement>(focusableSelector));
    for (const el of items) {
      if (el.offsetParent !== null) { try { el.focus(); } catch {} break; }
    }
  };

  const handleKeyDown = (e: KeyboardEvent) => {
    if (!visible()) return;
    if (e.key === 'Escape') {
      e.preventDefault();
      props.onClose();
      return;
    }
    if (e.key === 'Tab' && panelRef) {
      const items = Array.from(panelRef.querySelectorAll<HTMLElement>(focusableSelector)).filter(el => el.tabIndex !== -1 && el.offsetParent !== null);
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement as HTMLElement | null;
      if (e.shiftKey) {
        if (active === first || !panelRef.contains(active)) { e.preventDefault(); first.focus(); }
      } else {
        if (active === last || !panelRef.contains(active)) { e.preventDefault(); first.focus(); }
      }
    }
  };

  onMount(() => {
    document.addEventListener('keydown', handleKeyDown, true);
  });
  onCleanup(() => {
    document.removeEventListener('keydown', handleKeyDown, true);
  });

  createEffect(async () => {
    if (props.open) {
      setVisible(true);
      queueMicrotask(() => {
        document.body.classList.add('modal-open');
        if (overlayRef) animate(overlayRef, { opacity: [0, 1] }, { duration: 0.22, easing: 'ease-out' });
        if (panelRef) animate(panelRef, { opacity: [0, 1], transform: ['translateY(8px) scale(0.985)', 'translateY(0) scale(1)'] }, { duration: 0.28, easing: [0.2, 0.8, 0.2, 1] }).finished.then(() => setInitialFocus());
        else setInitialFocus();
      });
    } else if (visible()) {
      const tasks: Promise<any>[] = [];
      if (overlayRef) tasks.push(animate(overlayRef, { opacity: [1, 0] }, { duration: 0.18, easing: 'ease-in' }).finished);
      if (panelRef) tasks.push(animate(panelRef, { opacity: [1, 0], transform: ['translateY(0) scale(1)', 'translateY(8px) scale(0.985)'] }, { duration: 0.20, easing: [0.4, 0.0, 1, 1] }).finished);
      try { await Promise.all(tasks); } catch {}
      document.body.classList.remove('modal-open');
      setVisible(false);
    }
  });

  const onCloseClick = () => props.onClose();
  const handleOverlayClick = (e: MouseEvent) => {
    if (e.target === overlayRef) props.onClose();
  };

  return (
    <Show when={visible()}>
      <Portal>
        <div
          ref={overlayRef}
          class="mp-modal-overlay"
          data-modal-id={props.id}
          onClick={handleOverlayClick}
        >
          <div
            ref={panelRef}
            class={`mp-modal-panel ${props.size || 'md'}`}
            role="dialog"
            aria-modal="true"
            aria-labelledby={`${props.id}-title`}
            onClick={(e) => e.stopPropagation()}
          >
            <div class="mp-modal-header">
              <h2 id={`${props.id}-title`} class="mp-modal-title">{props.title}</h2>
              <button class="mp-close-btn" aria-label="Close" onClick={onCloseClick}>
                <span aria-hidden="true">×</span>
              </button>
            </div>
            <div class="mp-modal-body">
              {props.children}
            </div>
          </div>
        </div>
      </Portal>
    </Show>
  );
}
